import React from 'react'
import {useUserRoles} from '../../../../utils/use-user-roles'
import {useUserFeatures} from '../../../../context/user-features-context'
import {isCallSkippable} from '../utils/skipCallEligibility'
import Icon from '../../../../core/shared/Icon'
import {
  ScheduledNotice,
  ScheduledNoticeText,
  ResetButton,
  ScheduleCallButton,
} from './SchedulingStep.styles'

// The "Skip" control shown at the trailing end of CallSectionTitle's row
// (next to "Thryv Kickoff Call" / "Thryv Success Services Call") for one
// location + named call — owns its own eligibility check (isCallSkippable)
// so SchedulingStep/LocationScheduleDetail don't need to know the rule, only
// that a Skip control may or may not render here. Renders nothing when this
// call isn't skippable for this location/role/flag combination.
export function SkipCallButton({
  location,
  callType,
  callTypesForLocation,
  onSkip,
}) {
  const {roles} = useUserRoles()
  const features = useUserFeatures()

  const hasKickoffCall = (callTypesForLocation ?? []).some(
    ct => ct.key === 'kickoff',
  )

  const skippable = isCallSkippable({
    location,
    callType: callType.key,
    hasKickoffCall,
    roles,
    features,
  })

  if (!skippable) return null

  // Rendered beside "Schedule Call" in ScheduleActionsRow, so it reuses
  // ScheduleCallButton's exact style (primary pill, uppercase) to match it.
  return (
    <ScheduleCallButton
      variant="primary"
      size="sm"
      type="button"
      onClick={onSkip}
      aria-label={`Skip ${callType.label}`}
      leftIcon={<Icon name="fwdArrow" size="sm" color="#fff" />}
    >
      Skip
    </ScheduleCallButton>
  )
}

// Replaces the calendar/slot picker once a call has been skipped — reuses
// ScheduledNotice's exact shell (same strip a real booking renders in) so a
// skipped call reads as "handled" rather than as an error state, with an
// Undo control (mirrors legacy ConfirmedCall's "Edit" on a skipped call,
// which un-skips and returns to the calendar) in place of Reset.
export function SkippedCallNotice({callType, onUndoSkip}) {
  return (
    <ScheduledNotice>
      <Icon name="info" size="md" />
      <ScheduledNoticeText>
        <u>{callType.label}</u> has been skipped.
      </ScheduledNoticeText>
      <ResetButton
        type="button"
        onClick={onUndoSkip}
        aria-label={`Undo skip for ${callType.label}`}
      >
        <Icon name="reset" size="sm" />
        Undo
      </ResetButton>
    </ScheduledNotice>
  )
}
